/**
 * The only three operations that use the private key.
 *
 * ## Why login nonces are namespaced
 *
 * Canton asks the wallet to sign a bare 32-byte transaction hash — an opaque blob with no
 * self-describing structure. If a login challenge were also an arbitrary blob, a compromised
 * (or malicious) backend could hand the wallet a real transfer hash, call it a "login
 * challenge", and get it signed without the user ever seeing a transaction.
 *
 * So the two namespaces are kept provably disjoint:
 *   - a login challenge is signed as **UTF-8 text** and MUST start with `nuxaris-login:`;
 *   - a transaction is signed as **32 raw bytes** decoded from base64, and is refused if
 *     those bytes happen to spell out a login challenge.
 *
 * A 32-byte Canton hash can never satisfy the first rule, and a login string can never
 * satisfy the second, so neither signing path can be tricked into producing the other's
 * signature.
 */

import { ed25519 } from '@noble/curves/ed25519';

import { toBase64, fromBase64, toHex, fromHex, utf8ToBytes, wipe } from './bytes';
import { keyMaterialFromSecret } from './keys';
import { InvalidNonceError, InvalidPreparedTxError, InvalidKeyError } from './errors';
import { requireSecret, type Session } from './session';

/** Mandatory prefix of every login challenge issued by the auth backend. */
export const LOGIN_NONCE_PREFIX = 'nuxaris-login:';

/** Length of a Canton prepared-transaction hash, in bytes. */
export const PREPARED_TX_HASH_LENGTH = 32;

/** Key material as handed back by {@link exportSecret}. */
export interface ExportedSecret {
  /** Present only when the wallet was created from / imported as a BIP-39 phrase. */
  mnemonic?: string;
  /** Raw Ed25519 private scalar, 64 hex chars — the same format as legacy recovery keys. */
  privateKeyHex: string;
}

/**
 * Signs an authentication challenge.
 *
 * @throws {InvalidNonceError} if the challenge does not start with `nuxaris-login:`.
 * @returns the 64-byte Ed25519 signature, base64-encoded (the server verifies it with
 *          `crypto.verify(null, Buffer.from(nonce, 'utf8'), pubKey, sig)`).
 */
export function signLoginNonce(session: Session, nonce: string): string {
  if (typeof nonce !== 'string' || nonce.indexOf(LOGIN_NONCE_PREFIX) !== 0) {
    throw new InvalidNonceError(
      `Refusing to sign: a login challenge must start with "${LOGIN_NONCE_PREFIX}"`,
    );
  }
  const { privateKey } = requireSecret(session);
  return toBase64(ed25519.sign(utf8ToBytes(nonce), privateKey));
}

/** True when the bytes are the ASCII encoding of a login challenge. */
function looksLikeLoginChallenge(bytes: Uint8Array): boolean {
  const prefix = utf8ToBytes(LOGIN_NONCE_PREFIX);
  if (bytes.length < prefix.length) return false;
  for (let i = 0; i < prefix.length; i += 1) {
    if (bytes[i] !== prefix[i]) return false;
  }
  return true;
}

/**
 * Signs a Canton prepared transaction.
 *
 * @param preparedTxHashB64 the `preparedTransactionHash` returned by the ledger API —
 *        exactly 32 raw bytes, base64-encoded. The signature covers those **raw bytes**,
 *        not the base64 text.
 * @throws {InvalidPreparedTxError} if the blob is not 32 bytes, or if it spells out a
 *         login challenge (see the namespace argument at the top of this file).
 */
export function signPreparedTx(session: Session, preparedTxHashB64: string): string {
  let hash: Uint8Array;
  try {
    hash = fromBase64(preparedTxHashB64);
  } catch (err) {
    throw new InvalidPreparedTxError('Prepared transaction hash is not valid base64');
  }
  if (hash.length !== PREPARED_TX_HASH_LENGTH) {
    throw new InvalidPreparedTxError(
      `Prepared transaction hash must be exactly ${PREPARED_TX_HASH_LENGTH} raw bytes, got ${hash.length}`,
    );
  }
  if (looksLikeLoginChallenge(hash)) {
    throw new InvalidPreparedTxError('Refusing to sign a login challenge through the transaction path');
  }
  const { privateKey } = requireSecret(session);
  return toBase64(ed25519.sign(hash, privateKey));
}

/**
 * Signs a Canton topology hash, for the three transactions that create a party.
 *
 * The hash arrives hex-encoded and multihash-framed (`1220…`), so it is neither 32 bytes
 * nor text: it cannot collide with either of the two other signing paths. The signature
 * covers the decoded bytes and is returned hex-encoded, which is what the validator expects.
 *
 * @throws {InvalidPreparedTxError} if the value is not plausible topology hex.
 */
export function signTopologyHash(session: Session, hashHex: string): string {
  if (typeof hashHex !== 'string' || !/^[0-9a-fA-F]{40,140}$/.test(hashHex) || hashHex.length % 2 !== 0) {
    throw new InvalidPreparedTxError('Topology hash must be an even-length hex string');
  }
  const bytes = fromHex(hashHex);
  if (looksLikeLoginChallenge(bytes)) {
    throw new InvalidPreparedTxError('Refusing to sign a login challenge through the topology path');
  }
  const { privateKey } = requireSecret(session);
  return toHex(ed25519.sign(bytes, privateKey));
}

/**
 * Signs a login challenge straight from a secret, without a vault.
 *
 * Needed for exactly one moment: the first sign-in on a fresh browser, where the user
 * pastes their recovery key and must authenticate *before* an account id exists to key a
 * vault by. The key is derived, used and wiped inside this call — it is never returned,
 * never stored, and no session is created.
 *
 * @param secret a 24-word BIP-39 phrase or a 64-hex private key.
 * @throws {InvalidNonceError} if the challenge lacks the `nuxaris-login:` prefix.
 */
export function signLoginNonceWithSecret(secret: string, nonce: string): string {
  if (typeof nonce !== 'string' || nonce.indexOf(LOGIN_NONCE_PREFIX) !== 0) {
    throw new InvalidNonceError(
      `Refusing to sign: a login challenge must start with "${LOGIN_NONCE_PREFIX}"`,
    );
  }
  const material = keyMaterialFromSecret(secret);
  try {
    return toBase64(ed25519.sign(utf8ToBytes(nonce), material.privateKey));
  } finally {
    wipe(material.privateKey);
  }
}

/**
 * The **only** way key material leaves this module: for the "reveal your recovery phrase"
 * screen. Callers must never persist, log or transmit the result.
 */
export function exportSecret(session: Session): ExportedSecret {
  const secret = requireSecret(session);
  const exported: ExportedSecret = { privateKeyHex: toHex(secret.privateKey) };
  if (secret.mnemonic) exported.mnemonic = secret.mnemonic;
  return exported;
}

/**
 * Verifies a base64 signature against a hex public key. Purely local sanity checking
 * (tests, "does this phrase really control this party?" flows); never throws.
 */
export function verifySignature(publicKeyHex: string, message: Uint8Array, signatureB64: string): boolean {
  try {
    const publicKey = /^[0-9a-fA-F]{64}$/.test(publicKeyHex)
      ? publicKeyHex
      : (() => {
          throw new InvalidKeyError('Public key must be 64 hex characters');
        })();
    return ed25519.verify(fromBase64(signatureB64, 64), message, publicKey);
  } catch (err) {
    return false;
  }
}
